import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Store } from '@ngrx/store';
import {
  addStudioImage,
  addStudioVideo,
  deleteStudioImage,
  deleteStudioVideo,
  selectStudioImages,
  selectStudioVideos,
  setChangeTookPlace,
  StudioImage,
  StudioService,
  StudioVideo,
  StudioVideoShapeDescriptor,
  VideoInstance,
} from '../../element-editor/store/studio';
import { ShapeService } from '../../element-editor/shape/shape.service';
import { VideoTimeRange } from '../video-preview/video-preview.component';
import { omit } from 'lodash';
import { StudioLayoutService } from '../../element-editor/store/studio/layout';
import { FileService } from '../../services/file/file.service';

export type MediaType = 'image' | 'video';

@Component({
  selector: 'nw-media-upload-modal',
  templateUrl: './media-upload-modal.component.html',
  styleUrls: ['./media-upload-modal.component.scss'],
})
export class MediaUploadModalComponent implements OnInit {
  @Input() type: MediaType = 'image';
  @Input() selectedMediaIds: string[] = [];

  @Output() cancel = new EventEmitter<void>();
  @Output() submit = new EventEmitter<string[]>();

  images: StudioImage[] = [];
  videos: StudioVideo[] = [];
  selectedIds: Set<string> = new Set();

  // Delete confirmation state
  showDeleteConfirmation = false;
  mediaToDelete: StudioImage | StudioVideo | null = null;

  // Upload state
  isDragging = false;
  isUploading = false;
  // Video preview state
  previewingVideo: StudioVideo | null = null;
  videoTimeRanges: Map<string, VideoTimeRange> = new Map();
  constructor(
    private store: Store,
    private shapeService: ShapeService,
    private readonly service: StudioService,
    private layoutService: StudioLayoutService,

    private fileService: FileService,
  ) {
    // -- //
  }

  ngOnInit() {
    // Initialize selected IDs from input // -- //
    this.selectedIds = new Set(this.selectedMediaIds);

    // Subscribe to images from store
    this.store.select(selectStudioImages).subscribe(images => {
      this.images = Object.values(images);
    });

    // Subscribe to videos from store
    this.store.select(selectStudioVideos).subscribe(videos => {
      this.videos = Object.values(videos);
    });
  }

  getMediaUrl(media: StudioImage | StudioVideo): string {
    // Check if currently uploading (use local data URL)
    if (this.currentlyUploadingImages[media.id]) {
      return this.currentlyUploadingImages[media.id];
    }
    if (media.url) {
      return media.url;
    }
    // Fallback to S3 URL pattern if s3Id exists
    if (media.id) {
      return `https:/private.cdn.inwedio.com/images/${media.id}`;
    }
    return '';
  }

  isSelected(mediaId: string): boolean {
    return this.selectedIds.has(mediaId);
  }

  toggleSelection(mediaId: string) {
    if (this.selectedIds.has(mediaId)) {
      this.selectedIds.delete(mediaId);
    } else {
      this.selectedIds.add(mediaId);
    }
  }

  previewVideoInstance: VideoInstance | null = null;
  onVideoClick(video: StudioVideo) {
    console.log('onVideoClick', video);
    // For videos, show preview instead of just toggling selection // -- // -- //

    const { instances } = video;
    const lastInstanceByEnd = Object.values(instances || {})
      .sort((a, b) => b.to - a.to)
      .pop();

    let from: number, to: number;
    if (lastInstanceByEnd) {
      from = lastInstanceByEnd.to;
      to = lastInstanceByEnd.to + (video.duration - lastInstanceByEnd.from) / 2;
    } else {
      from = 0;
      to = video.duration / 2;
    }

    if (this.type === 'video') {
      const videoInstance: StudioVideoShapeDescriptor = {
        id: this.shapeService.getNewID(),
        videoId: video.id,
        from,
        to,
        cell: {
          x: 0,
          y: 0,
          w: 1,
          h: 1,
        },
      };

      // -- //

      this.layoutService.addVideo(videoInstance);
      this.onClose();
      // -- //
      // this.previewingVideo = video;
      // // Auto-select the video when previewing
      // if (!this.selectedIds.has(video.id)) {
      //   this.selectedIds.add(instanceId);
      // }
    }
  }

  onDeleteClick(event: Event, media: StudioImage | StudioVideo) {
    event.stopPropagation();
    this.mediaToDelete = media;
    this.showDeleteConfirmation = true;
  }

  cancelDelete() {
    this.showDeleteConfirmation = false;
    this.mediaToDelete = null;
  }

  confirmDelete() {
    if (this.mediaToDelete) {
      // Remove from selection if selected
      this.selectedIds.delete(this.mediaToDelete.id);
      // Dispatch delete action based on type
      if (this.type === 'image') {
        this.store.dispatch(
          deleteStudioImage({ imageId: this.mediaToDelete.id }),
        );
      } else {
        this.store.dispatch(
          deleteStudioVideo({ videoId: this.mediaToDelete.id }),
        );
      }
    }
    this.showDeleteConfirmation = false;
    this.mediaToDelete = null;
  }

  onClose() {
    this.cancel.emit();
  }

  onSubmit() {
    this.layoutService.imagesChanged(Array.from(this.selectedIds)); // --
    this.submit.emit(Array.from(this.selectedIds));
  }

  // Drag and drop handlers
  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;

    const files = event.dataTransfer?.files;
    if (files) {
      this.handleFiles(files);
    }
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      this.handleFiles(input.files);
    }
  }

  handleFiles(files: FileList) {
    Array.from(files).forEach(file => {
      if (this.type === 'image' && file.type.startsWith('image/')) {
        this.uploadImageFile(file);
      } else if (this.type === 'video' && file.type.startsWith('video/')) {
        this.uploadVideoFile(file);
      }
    });
  }

  async uploadImageFile(file: File) {
    const processed = await this.fileService.processFile(file);
    const { meta, preview } = processed;
    this.currentlyUploadingImages[meta.id] = preview?.objectUrl;

    // Add to studio store immediately so thumbnail is visible
    this.store.dispatch(addStudioImage({ image: omit(meta, 'url') }));
    this.store.dispatch(setChangeTookPlace());
    this.fileService.uploadFile(processed);
  }

  async uploadVideoFile(file: File) {
    const processed = await this.fileService.processFile(file);
    const { meta, preview } = processed;
    this.currentlyUploadingVideos[meta.id] = preview?.objectUrl;

    // Add to studio store immediately so thumbnail is visible
    this.store.dispatch(addStudioVideo({ video: omit(meta, 'url') }));
    this.store.dispatch(setChangeTookPlace());
    this.fileService.uploadFile(processed);
  }

  private async getFirstFrameThumbnailUrl(
    videoEl: HTMLVideoElement,
    opts?: {
      seekTo?: number;
      maxWidth?: number;
      mimeType?: string;
      quality?: number;
    },
  ): Promise<string> {
    const seekTo = opts?.seekTo ?? 0.1; // 0.1s avoids many black-first-frame cases
    const maxWidth = opts?.maxWidth ?? 480; // keep it light
    const mimeType = opts?.mimeType ?? 'image/jpeg';
    const quality = opts?.quality ?? 0.82;

    // Wait until the target frame is seeked
    const targetTime = Math.min(
      Math.max(seekTo, 0),
      Math.max(videoEl.duration - 0.01, 0),
    );

    await new Promise<void>((resolve, reject) => {
      const onSeeked = () => {
        cleanup();
        resolve();
      };
      const onError = () => {
        cleanup();
        reject(new Error('Video seek error'));
      };
      const cleanup = () => {
        videoEl.removeEventListener('seeked', onSeeked);
        videoEl.removeEventListener('error', onError);
      };

      videoEl.addEventListener('seeked', onSeeked, { once: true });
      videoEl.addEventListener('error', onError, { once: true });

      // trigger seek
      videoEl.currentTime = targetTime;
    });

    const srcW = videoEl.videoWidth;
    const srcH = videoEl.videoHeight;

    const scale = Math.min(1, maxWidth / srcW);
    const outW = Math.round(srcW * scale);
    const outH = Math.round(srcH * scale);

    const canvas = document.createElement('canvas');
    canvas.width = outW;
    canvas.height = outH;

    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Could not get canvas 2d context');

    ctx.drawImage(videoEl, 0, 0, outW, outH);

    // Data URL is simplest to bind into <img>. If you prefer Blob URL, I’ll give that variant too.
    return canvas.toDataURL(mimeType, quality);
  }

  private dataUrlToBlob(dataUrl: string): Blob {
    const [header, base64] = dataUrl.split(',');
    const mimeMatch = header.match(/data:(.*?);base64/);
    const mime = mimeMatch?.[1] ?? 'image/jpeg';

    const binary = atob(base64);
    const len = binary.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) bytes[i] = binary.charCodeAt(i);

    return new Blob([bytes], { type: mime });
  }

  private blobToFile(blob: Blob, filename: string): File {
    return new File([blob], filename, { type: blob.type });
  }

  currentlyUploadingVideos: Record<string, string> = {};
  currentlyUploadingImages: Record<string, string> = {};

  getThumbnailUrl(video: StudioVideo): string {
    if (this.currentlyUploadingVideos[video.id]) {
      return this.currentlyUploadingVideos[video.id];
    }
    return `https://private.cdn.inwedio.com/videos/${video.id}/thumbnail.jpg`;
  }

  _uploadVideoFile(file: File) {
    const id = this.shapeService.getNewID();
    const extension = file.name.split('.').pop() || '';

    this.isUploading = true;

    const objectUrl = URL.createObjectURL(file);

    const video = document.createElement('video');
    video.preload = 'metadata';
    video.muted = true;
    video.playsInline = true;

    // -- //
    video.onloadedmetadata = async () => {
      try {
        // 1) Generate local thumbnail for immediate UI
        const tmpThumbnailUrl = await this.getFirstFrameThumbnailUrl(video, {
          seekTo: 0.1,
          maxWidth: 480,
          mimeType: 'image/jpeg',
          quality: 0.82,
        });

        this.currentlyUploadingVideos[id] = tmpThumbnailUrl;

        const thumbBlob = this.dataUrlToBlob(tmpThumbnailUrl);
        const thumbFile = this.blobToFile(thumbBlob, `${id}.jpg`); //

        this.shapeService.cs.httpService
          .saveFileToS3(thumbFile, `videos/${id}/thumbnail.jpg`)
          .then(() => {
            console.log('Thumbnail uploaded to S3:', id);
          });

        // 2) Ask backend for a pre-signed PUT URL
        // You can route this through whatever client you already have.
        // Example assumes you have an Angular HttpClient on httpService.
        const { data: presign } =
          await this.shapeService.cs.httpService._postPromise<{
            url: string;
            key: string;
          }>('file/presign-put', {
            uuid: id,
            name: file.name,
            // contentType: file.type,
            type: 'video',
          });

        console.log('presign', presign);
        // 3) Create studio video (use objectUrl now; store s3Key for later retrieval)
        const studioVideo: StudioVideo = {
          id,
          s3Id: id,
          url: objectUrl, // local preview right after selection
          extension,
          sizeInMb: file.size / (1024 * 1024),
          width: video.videoWidth,
          height: video.videoHeight,
          duration: video.duration,
          tmpThumbnailUrl,
          fileName: file.name,
        };

        // -- //

        this.store.dispatch(
          addStudioVideo({
            video: { ...studioVideo, tmpThumbnailUrl: undefined },
          }),
        );
        this.store.dispatch(setChangeTookPlace());

        // 4) Upload directly to S3 using the pre-signed PUT url
        // IMPORTANT: Content-Type must match what was used to sign the URL.
        const putRes = await fetch(presign.url.video, {
          method: 'PUT',
          headers: {
            'Content-Type': file.type || 'application/octet-stream',
          },
          body: file,
        });

        if (!putRes.ok) {
          throw new Error(
            `S3 PUT failed: ${putRes.status} ${putRes.statusText}`,
          );
        }

        console.log('Video uploaded to S3 via presigned PUT:', {
          id,
          key: presign.key,
        });
        this.isUploading = false;

        // Optional: if you want to stop using the objectUrl after upload, you could later swap url to a presigned GET.
        // But if you want instant scrubbing without extra calls, keeping objectUrl is fine while editor is open.
      } catch (error) {
        console.error('Failed to upload video via presigned PUT:', error);

        this.store.dispatch(deleteStudioVideo({ videoId: id }));
        URL.revokeObjectURL(objectUrl);
        this.isUploading = false;

        alert('Failed to upload video. Please try again.');
      }
    };

    video.onerror = e => {
      console.error('Failed to load video metadata', e);
      URL.revokeObjectURL(objectUrl);
      this.isUploading = false;
      alert('Failed to load video. The file may be corrupted or unsupported.');
    };

    video.src = objectUrl;
    video.load();
  }

  triggerFileInput() {
    const fileInput = document.getElementById(
      'media-file-input',
    ) as HTMLInputElement;
    fileInput?.click();
  }

  onVideoSaved(videoInstance: VideoInstance) {
    this.previewingVideo = null;

    // this.layoutService.addVideo(videoInstance);

    this.submit.emit([videoInstance.id]);
  }

  onVideoCancelled() {
    console.log('onVideoCancelled');
    this.previewVideoInstance = null;
    this.previewingVideo = null;
  }
  onSaved() {
    // -- //
  }
}
